import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { ItemCardCountdown } from '@/components/GridItemCard/Card/components/ItemCardCountdown/ItemCardCountdown';
import { WeakRouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummarySellerId, getItemSummarySellerName } from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardNavigationTrigger } from '@/utils/amplitude/analytics';
import { getSellerUrl, isMyBidsPage, isSavedItemsPage } from '@/utils/urls';

export type CardInfoProps = {
    hasNewSimilarItemsIconCTA?: boolean;
    hasNewSimilarItemsTextCTA?: boolean;
    isMobile: boolean;
    isOpenItem: boolean;
    itemId: number;
    onNavigate?: (trigger: ItemCardNavigationTrigger) => void;
};

const CardInfo = ({
    hasNewSimilarItemsIconCTA = false,
    hasNewSimilarItemsTextCTA = false,
    isMobile,
    isOpenItem,
    itemId,
    onNavigate,
}: CardInfoProps) => {
    const { pathname } = useLocation();
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));

    // Displays Card Info in Timed Plus item cards on Search, and My Items Pages.
    // On My Items Pages, we show the auction house name because there are no star ratings.
    // On Search Pages, we hide the auction house name because the star ratings already show it.
    const shouldShowAuctionHouseName = isMyBidsPage(pathname) || isSavedItemsPage(pathname);

    return (
        <PlacedItemInfo data-testid="timedPlusMyItemsCardInfo">
            <StyledDateInfo>
                <ItemCardCountdown
                    className={cn({ 'text-xs smMin:text-sm': hasNewSimilarItemsIconCTA || hasNewSimilarItemsTextCTA })}
                    hasNewSimilarItemsIconCTA={hasNewSimilarItemsIconCTA}
                    hasNewSimilarItemsTextCTA={hasNewSimilarItemsTextCTA}
                    itemId={itemId}
                    onNavigate={() => onNavigate?.(ItemCardNavigationTrigger.CountdownClick)}
                />
            </StyledDateInfo>
            {shouldShowAuctionHouseName && isOpenItem && !isMobile && (
                <StyledAuctionHouseName
                    data-testid="auction-house-name-container"
                    onClick={() => onNavigate?.(ItemCardNavigationTrigger.HouseNameClick)}
                    to={getSellerUrl(sellerId, sellerName)}
                >
                    <BodySecondary color="secondary">{sellerName}</BodySecondary>
                </StyledAuctionHouseName>
            )}
        </PlacedItemInfo>
    );
};

export default CardInfo;

const PlacedItemInfo = styled.section`
    grid-area: iteminfo;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs};
`;

const StyledDateInfo = styled.span`
    line-height: 18px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 100%;
    }
    @media (max-width: 375px) {
        width: unset;
    }
`;

const StyledAuctionHouseName = styled(WeakRouteLink)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 18px;
    text-align: right;
    max-width: 140px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        min-width: 94px;
        max-width: 94px;
    }

    &:hover > span {
        color: ${({ theme }) => theme.colors.blue100};
        text-decoration: underline;
    }
`;
